import React, { memo, useCallback, useMemo } from 'react'
import SavedLaneForm from 'transfix-form/forms/SavedLaneForm'
import { ISavedLaneFormProps } from 'transfix-form/forms/SavedLaneForm/SavedLaneForm'
import { TSavedLaneSchema } from 'transfix-form/forms/SavedLaneForm/schema'
import { IAppTypes } from 'transfix-form/forms/SavedLaneForm/types'
import { FormModal, IFormModalProps } from 'transfix-ui/components/Modals'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useSavedLaneMutations } from 'apps/fleet-planner/hooks/useSavedLaneMutations'
import { BookableShipmentSavedSearch } from 'apps/fleet-planner/types'
import { getFMSSavedLaneSchema } from '../utils'

const APP = IAppTypes.fms
const FAILURE_MESSAGE = 'Server error. Contact tech support for assistance.'
const ADD_SUCCESS_MESSAGE = 'Lane info saved successfully'
const EDIT_SUCCESS_MESSAGE = 'Lane info updated successfully'

export interface ISavedLaneFormModalProps extends Pick<IFormModalProps, 'onClose'> {
  savedLane?: BookableShipmentSavedSearch
  onSuccess: (updatedSavedLane: BookableShipmentSavedSearch) => void
}

const SavedLaneFormModal = memo<ISavedLaneFormModalProps>(({ onClose, onSuccess, savedLane }) => {
  const { trackEvent: trackFMSEvent } = useFMSAnalytics()
  const { openToast } = useGlobalToast()

  const { hasNotificationsEnabled: currentSavedLaneNotifications, id: savedLaneId } =
    savedLane || {}

  const { createSavedLane, loading, updateSavedLane } = useSavedLaneMutations({ savedLaneId })

  // Determine if the form is being used to edit a saved lane
  const isEditing = Boolean(savedLane)

  // Get default form values if form is being used for editing
  const defaultFormValues: ISavedLaneFormProps['defaultValues'] = useMemo(() => {
    if (!isEditing || !savedLane) {
      return undefined
    }
    return getFMSSavedLaneSchema(savedLane)
  }, [isEditing, savedLane])

  const showErrorToast = useCallback(() => {
    openToast({ toastMessage: FAILURE_MESSAGE, toastType: 'alert' })
  }, [openToast])

  const showSuccessToast = useCallback(() => {
    openToast({ toastMessage: isEditing ? EDIT_SUCCESS_MESSAGE : ADD_SUCCESS_MESSAGE })
  }, [isEditing, openToast])

  const trackSuccessEvent = useCallback(
    (savedLaneResponse: BookableShipmentSavedSearch) => {
      if (isEditing) {
        const { hasNotificationsEnabled: updatedSavedLaneNotifications } = savedLaneResponse || {}

        // If the notification setting was changed
        if (updatedSavedLaneNotifications !== currentSavedLaneNotifications) {
          trackFMSEvent(
            'Saved Lanes',
            updatedSavedLaneNotifications
              ? 'TURNED_ON_SAVED_LANE_NOTIFICATIONS'
              : 'TURNED_OFF_SAVED_LANE_NOTIFICATIONS'
          )
        }

        trackFMSEvent('Saved Lanes', 'UPDATED_SAVED_LANE')
      } else {
        trackFMSEvent('Saved Lanes', 'CREATED_SAVED_LANE')
      }
    },
    [currentSavedLaneNotifications, isEditing, trackFMSEvent]
  )

  const submitHandler = useCallback(
    async (savedLane: TSavedLaneSchema) => {
      try {
        const response = isEditing
          ? await updateSavedLane(savedLane)
          : await createSavedLane(savedLane)
        if (response) {
          trackSuccessEvent(response)
          showSuccessToast()
          onSuccess(response)
        } else {
          showErrorToast()
        }
      } catch {
        showErrorToast()
      }
    },
    [
      isEditing,
      updateSavedLane,
      createSavedLane,
      trackSuccessEvent,
      showSuccessToast,
      onSuccess,
      showErrorToast,
    ]
  )

  const errorHandler = useCallback(
    ({ errors, formValues }) => {
      if (isEditing) {
        trackFMSEvent('Saved Lanes', 'ERROR_UPDATING_SAVED_LANE', {
          savedLaneId,
          errors,
          formValues,
        })
      } else {
        trackFMSEvent('Saved Lanes', 'ERROR_CREATING_SAVED_LANE', { errors, formValues })
      }
    },
    [isEditing, savedLaneId, trackFMSEvent]
  )

  return (
    <FormModal title='Saved Lane' dataTest='saved-lane-fms-modal' onClose={onClose} open>
      <SavedLaneForm
        app={APP}
        loading={loading}
        defaultValues={defaultFormValues}
        onSubmit={submitHandler}
        onError={errorHandler}
        onCancel={onClose}
      />
    </FormModal>
  )
})

export default SavedLaneFormModal
